// Cleaning task dashboard, item scores and chain refresh runs.
import {
  CleaningAssignMatchingRequest,
  CleaningAssignMatchingResponse,
  CleaningChainHistoryResponse,
  CleaningChainStatsResponse,
  CleaningDashboardResponse,
  CleaningEnabledModFilter,
  CleaningFamily,
  CleaningFinding,
  CleaningIssueScope,
  CleaningIssueType,
  CleaningItemScore,
  CleaningItemsResponse,
  CleaningLockFilter,
  CleaningLockStatus,
  CleaningRefreshRun,
  CleaningSeverity,
  CleaningSort,
  CleaningSortDirection,
  ItemModPrefixEntry,
  ItemModPrefixesResponse,
} from '../../types/cleaningTasks';
import type { CleaningToolReviewIssueType } from '../../types/reviewIssues';
import { ForgeApiError, forgeBlobRequest, forgeRequest } from '../forgeRequest';
import { FORGE_ADMIN_API_URL, FORGE_CLEANING_API_URL } from './endpoints';

type CleaningItemsQueryParams = {
  taskView?: 'all' | 'live' | 'mine' | 'finished';
  taskStatus?: 'all' | 'open' | 'claimed' | 'done' | 'skipped' | 'superseded';
  assignee?: string[];
  /** Only tasks nobody is assigned; the server refuses it together with `assignee`. */
  unassigned?: boolean;
  claimant?: string[];
  finishedFrom?: string;
  finishedTo?: string;
  chainId: number;
  page?: number;
  pageSize?: number;
  severity?: CleaningSeverity | 'all';
  family?: CleaningFamily;
  sort?: CleaningSort;
  sortDirection?: CleaningSortDirection;
  search?: string;
  excluded?: 'hide' | 'only';
  duplicates?: 'hide' | 'show';
  issueTypes?: CleaningIssueType[];
  issueScope?: CleaningIssueScope | 'all';
  locks?: CleaningLockFilter;
  modPrefixes?: string[];
  enabledMod?: CleaningEnabledModFilter;
  /** Keeps only the items of this pilot project; live views only. */
  pilot?: string;
  reviewIssueTypes?: CleaningToolReviewIssueType[];
  /** Focus filter: items assessed on, or flagged for, these subscores. */
  focusSubscores?: string[];
  limit?: number | null;
};

function buildCleaningItemsQuery(params: CleaningItemsQueryParams): URLSearchParams {
  const query = new URLSearchParams({ chain_id: String(params.chainId) });
  if (params.taskView) query.set('task_view', params.taskView);
  if (params.taskStatus && params.taskStatus !== 'all') query.set('task_status', params.taskStatus);
  params.assignee?.forEach((email) => query.append('assignee', email));
  if (params.unassigned) query.set('unassigned', 'true');
  params.claimant?.forEach((email) => query.append('claimant', email));
  if (params.finishedFrom) query.set('finished_from', params.finishedFrom);
  if (params.finishedTo) query.set('finished_to', params.finishedTo);
  if (params.page !== undefined) query.set('page', String(params.page));
  if (params.pageSize !== undefined) query.set('page_size', String(params.pageSize));
  if (params.severity !== undefined) query.set('severity', params.severity);
  if (params.family !== undefined) query.set('family', params.family);
  if (params.sort !== undefined) query.set('sort', params.sort);
  if (params.sortDirection !== undefined) query.set('sort_direction', params.sortDirection);
  if (params.excluded !== undefined) query.set('excluded', params.excluded);
  if (params.duplicates !== undefined) query.set('duplicates', params.duplicates);
  params.issueTypes?.forEach((issueType) => query.append('issue_type', issueType));
  if (params.issueScope !== undefined) query.set('issue_scope', params.issueScope);
  if (params.locks !== undefined) query.set('locks', params.locks);
  if (params.enabledMod && params.enabledMod !== 'all') query.set('enabled_mod', params.enabledMod);
  if (params.pilot) query.set('pilot', params.pilot);
  params.modPrefixes?.forEach((prefix) => query.append('mod_prefix', prefix));
  params.reviewIssueTypes?.forEach((issueType) => query.append('review_issue_type', issueType));
  params.focusSubscores?.forEach((subscore) => query.append('focus_subscore', subscore));
  if (params.limit !== undefined && params.limit !== null) query.set('limit', String(params.limit));
  const search = params.search?.trim();
  if (search) query.set('search', search);
  return query;
}

type CleaningSubscoresWire = CleaningItemScore['subscores'] & {
  class_coherence?: number | null;
  class_confusion?: number | null;
  front_split?: number | null;
  front_strays?: number | null;
  front_fragmentation?: number | null;
  front_confusion?: number | null;
  duplicate_front_classes?: number | null;
  front_identity?: number | null;
  identity_resolution?: number | null;
  review_flags?: number | null;
  metadata_integrity?: number | null;
  barcode_mix?: number | null;
  carton_confusion?: number | null;
  nonfront_confusion?: number | null;
  partial_group_front_alignment?: number | null;
  nonfront_group_alignment?: number | null;
  review_load?: number | null;
};

type CleaningFindingWire = NonNullable<CleaningItemScore['finding']> & {
  design_id?: string | null;
  issue_type?: CleaningFinding['issueType'];
  issue_scope?: CleaningFinding['issueScope'];
};

type CleaningItemCountsWire = CleaningItemScore['counts'] & {
  mixed_barcodes?: number;
  review_flagged?: number;
  metadata_issues?: number;
  review_issue_counts?: CleaningItemScore['counts']['reviewIssueCounts'];
  oldest_unresolved_created_at?: string | null;
};

type CleaningItemScoreWire = Omit<
  CleaningItemScore,
  'subscores' | 'finding' | 'findings' | 'counts' | 'taskCoveredBy'
> & {
  exclusion_outdated?: boolean;
  previous_score?: number | null;
  previous_severity?: string | null;
  mod_prefix?: string | null;
  locked_design_count?: number | null;
  lock_status?: CleaningLockStatus | null;
  taskCoveredBy?: CleaningItemScore['taskCoveredBy'];
  task_covered_by?: CleaningItemScore['taskCoveredBy'];
  subscores: CleaningSubscoresWire;
  finding?: CleaningFindingWire | null;
  findings?: CleaningFindingWire[];
  counts: CleaningItemCountsWire;
};

function normalizeCleaningFinding(finding: CleaningFindingWire): CleaningFinding {
  const { design_id, issue_type, issue_scope, ...findingRest } = finding;
  return {
    ...findingRest,
    designId: findingRest.designId ?? design_id,
    issueType: findingRest.issueType ?? issue_type,
    issueScope: findingRest.issueScope ?? issue_scope,
  };
}

function normalizeCleaningItem(item: CleaningItemScoreWire): CleaningItemScore {
  const {
    exclusion_outdated,
    previous_score,
    previous_severity,
    mod_prefix,
    locked_design_count,
    lock_status,
    task_covered_by,
    findings,
    ...itemRest
  } = item;
  const {
    class_coherence,
    class_confusion,
    front_split,
    front_strays,
    front_fragmentation,
    front_confusion,
    duplicate_front_classes,
    front_identity,
    identity_resolution,
    review_flags,
    metadata_integrity,
    barcode_mix,
    carton_confusion,
    nonfront_confusion,
    partial_group_front_alignment,
    nonfront_group_alignment,
    review_load,
    ...subscores
  } = item.subscores;
  const {
    mixed_barcodes,
    review_flagged,
    metadata_issues,
    review_issue_counts,
    oldest_unresolved_created_at,
    ...counts
  } = item.counts;
  const finding = item.finding ? normalizeCleaningFinding(item.finding) : item.finding;

  return {
    ...itemRest,
    exclusionOutdated: itemRest.exclusionOutdated ?? exclusion_outdated,
    previousScore: itemRest.previousScore !== undefined ? itemRest.previousScore : previous_score,
    previousSeverity:
      itemRest.previousSeverity !== undefined ? itemRest.previousSeverity : previous_severity,
    modPrefix: itemRest.modPrefix !== undefined ? itemRest.modPrefix : mod_prefix,
    lockedDesignCount:
      itemRest.lockedDesignCount !== undefined ? itemRest.lockedDesignCount : locked_design_count,
    lockStatus: itemRest.lockStatus !== undefined ? itemRest.lockStatus : lock_status,
    taskCoveredBy: itemRest.taskCoveredBy ?? task_covered_by,
    subscores: {
      ...subscores,
      classCoherence: subscores.classCoherence ?? class_coherence,
      classConfusion: subscores.classConfusion ?? class_confusion,
      frontSplit: subscores.frontSplit ?? front_split,
      frontStrays: subscores.frontStrays ?? front_strays,
      frontFragmentation: subscores.frontFragmentation ?? front_fragmentation,
      frontConfusion: subscores.frontConfusion ?? front_confusion,
      duplicateFrontClasses: subscores.duplicateFrontClasses ?? duplicate_front_classes,
      frontIdentity: subscores.frontIdentity ?? front_identity,
      identityResolution: subscores.identityResolution ?? identity_resolution,
      reviewFlags: subscores.reviewFlags ?? review_flags,
      metadataIntegrity: subscores.metadataIntegrity ?? metadata_integrity,
      barcodeMix: subscores.barcodeMix ?? barcode_mix,
      cartonConfusion: subscores.cartonConfusion ?? carton_confusion,
      nonfrontConfusion: subscores.nonfrontConfusion ?? nonfront_confusion,
      partialGroupFrontAlignment:
        subscores.partialGroupFrontAlignment ?? partial_group_front_alignment,
      nonfrontGroupAlignment: subscores.nonfrontGroupAlignment ?? nonfront_group_alignment,
      reviewLoad: subscores.reviewLoad ?? review_load,
    },
    finding,
    findings: findings?.map(normalizeCleaningFinding),
    counts: {
      ...counts,
      mixedBarcodes: counts.mixedBarcodes ?? mixed_barcodes,
      reviewFlagged: counts.reviewFlagged ?? review_flagged,
      metadataIssues: counts.metadataIssues ?? metadata_issues,
      reviewIssueCounts: counts.reviewIssueCounts ?? review_issue_counts,
      oldestUnresolvedCreatedAt:
        counts.oldestUnresolvedCreatedAt !== undefined
          ? counts.oldestUnresolvedCreatedAt
          : oldest_unresolved_created_at,
    },
  };
}

type CleaningItemsWireResponse = CleaningItemsResponse & {
  issue_type_counts?: CleaningItemsResponse['issueTypeCounts'];
  issue_scope_counts?: CleaningItemsResponse['issueScopeCounts'];
  mod_prefix_counts?: CleaningItemsResponse['modPrefixCounts'];
};

function normalizeCleaningItemsResponse(payload: CleaningItemsWireResponse): CleaningItemsResponse {
  return {
    ...payload,
    items: payload.items.map((item) => normalizeCleaningItem(item as CleaningItemScoreWire)),
    issueTypeCounts: payload.issueTypeCounts ?? payload.issue_type_counts,
    issueScopeCounts: payload.issueScopeCounts ?? payload.issue_scope_counts,
    modPrefixCounts: payload.modPrefixCounts ?? payload.mod_prefix_counts,
  };
}

export async function fetchCleaningDashboard(token: string): Promise<CleaningDashboardResponse> {
  return forgeRequest<CleaningDashboardResponse>(
    token,
    `${FORGE_CLEANING_API_URL}/dashboard`,
    'Error fetching cleaning tasks dashboard'
  );
}

/** Sort keys only an api_dl that serves column sorting accepts. */
const COLUMN_SORT_KEYS: readonly CleaningSort[] = [
  'identifier',
  'training_score',
  'inference_score',
  'catalog_score',
  'claimant',
  'assignee',
  'changed',
  'finished_at',
  'result',
  'bucket',
];

/**
 * Run a cleaning-items read, retrying once on the combined score when the deployed
 * api_dl predates column sorting and rejects the new keys. The list and its export
 * stay usable until the two sides line up.
 */
async function withLegacySortFallback<T>(
  params: CleaningItemsQueryParams,
  read: (params: CleaningItemsQueryParams) => Promise<T>
): Promise<T> {
  try {
    return await read(params);
  } catch (error) {
    const rejectedColumnSort =
      error instanceof ForgeApiError &&
      error.status === 400 &&
      params.sort !== undefined &&
      COLUMN_SORT_KEYS.includes(params.sort);
    if (!rejectedColumnSort) throw error;
    return read({ ...params, sort: 'score', sortDirection: undefined });
  }
}

export async function fetchCleaningItems(
  token: string,
  params: CleaningItemsQueryParams
): Promise<CleaningItemsResponse> {
  return withLegacySortFallback(params, async (sortedParams) =>
    normalizeCleaningItemsResponse(
      await forgeRequest<CleaningItemsWireResponse>(
        token,
        `${FORGE_CLEANING_API_URL}/items?${buildCleaningItemsQuery(sortedParams).toString()}`,
        'Error fetching cleaning tasks items'
      )
    )
  );
}

export async function exportCleaningItems(
  token: string,
  params: CleaningItemsQueryParams
): Promise<{ blob: Blob; filename: string }> {
  return withLegacySortFallback(params, (sortedParams) =>
    forgeBlobRequest(
      token,
      `${FORGE_CLEANING_API_URL}/items/export?${buildCleaningItemsQuery(sortedParams).toString()}`,
      'Error exporting cleaning tasks items',
      `cleaning-items-chain-${params.chainId}-${params.family ?? 'combined'}.csv`
    )
  );
}

/**
 * Set or clear the assignment of the top live tasks the list shows under these filters, across
 * all its pages, in one request, split round-robin among the assignees. A `dryRun` returns the
 * split and its fingerprint; the write sends the confirmed fingerprint back, and the server
 * answers 409 instead of assigning when the rows have changed since.
 */
export async function assignMatchingCleaningTasks(
  token: string,
  params: CleaningItemsQueryParams,
  request: CleaningAssignMatchingRequest
): Promise<CleaningAssignMatchingResponse> {
  return forgeRequest<CleaningAssignMatchingResponse>(
    token,
    `${FORGE_CLEANING_API_URL}/items/assign?${buildCleaningItemsQuery(params).toString()}`,
    'Error assigning cleaning tasks',
    { method: 'POST', body: request }
  );
}

export async function setCleaningItemExcluded(
  token: string,
  chainId: number,
  itemKey: string,
  excluded: boolean
): Promise<{ chain_id: number; item_key: string; excluded: boolean }> {
  return forgeRequest<{ chain_id: number; item_key: string; excluded: boolean }>(
    token,
    `${FORGE_CLEANING_API_URL}/exclusions`,
    'Error updating cleaning item exclusion',
    {
      method: 'PUT',
      body: {
        chain_id: chainId,
        item_key: itemKey,
        excluded,
      },
    }
  );
}

/**
 * Absent-resource reads: a 404 is a value, not a failure. `forgeRequest`
 * raises every non-ok status as a `ForgeApiError`, so the sentinel is restored
 * here; any other status keeps throwing with the transport's parsed message.
 */
async function nullOn404<T>(request: Promise<T>): Promise<T | null> {
  try {
    return await request;
  } catch (error) {
    if (error instanceof ForgeApiError && error.status === 404) return null;
    throw error;
  }
}

/** Completed refresh runs for one chain (oldest first); null on 404 (old API). */
export async function fetchCleaningChainHistory(
  token: string,
  chainId: number,
  limit = 60
): Promise<CleaningChainHistoryResponse | null> {
  return nullOn404(
    forgeRequest<CleaningChainHistoryResponse>(
      token,
      `${FORGE_CLEANING_API_URL}/chains/${chainId}/history?limit=${limit}`,
      'Error fetching cleaning tasks chain history'
    )
  );
}

/** Per-subscore aggregates for one chain; null when no snapshot is cached (404). */
export async function fetchCleaningChainStats(
  token: string,
  chainId: number
): Promise<CleaningChainStatsResponse | null> {
  return nullOn404(
    forgeRequest<CleaningChainStatsResponse>(
      token,
      `${FORGE_CLEANING_API_URL}/chains/${chainId}/stats`,
      'Error fetching cleaning tasks chain stats'
    )
  );
}

export async function refreshCleaningChain(
  token: string,
  chainId: number
): Promise<CleaningRefreshRun> {
  return forgeRequest<CleaningRefreshRun>(
    token,
    `${FORGE_CLEANING_API_URL}/refresh`,
    'Error refreshing cleaning tasks chain',
    {
      method: 'POST',
      body: {
        chain_id: chainId,
      },
    }
  );
}

export async function fetchCleaningRun(token: string, runId: string): Promise<CleaningRefreshRun> {
  return forgeRequest<CleaningRefreshRun>(
    token,
    `${FORGE_CLEANING_API_URL}/runs/${encodeURIComponent(runId)}`,
    'Error fetching cleaning tasks refresh run'
  );
}

export async function fetchItemModPrefixes(
  token: string,
  chainId: number,
  itemNumbers: string[]
): Promise<ItemModPrefixesResponse> {
  const query = new URLSearchParams({ chain_id: String(chainId) });
  itemNumbers.forEach((itemNumber) => query.append('item_number', itemNumber));
  return forgeRequest<ItemModPrefixesResponse>(
    token,
    `${FORGE_ADMIN_API_URL}/item-mod-prefixes?${query.toString()}`,
    'Error fetching item mod prefixes'
  );
}

export async function setItemModPrefix(
  token: string,
  chainId: number,
  itemNumber: string,
  modPrefix: string
): Promise<ItemModPrefixEntry> {
  return forgeRequest<ItemModPrefixEntry>(
    token,
    `${FORGE_ADMIN_API_URL}/item-mod-prefixes`,
    'Error saving item mod prefix',
    { method: 'PUT', body: { chain_id: chainId, item_number: itemNumber, mod_prefix: modPrefix } }
  );
}
